import React, { useEffect } from 'react';
import { Flex, Radio, SimpleGrid, FlexProps, RadioProps } from '@mantine/core';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import styled from 'styled-components';
import {
	SIGNATURE_FONT_FAMILIES,
	useSignatureGenerator,
} from '@dealstack/frontend/shared/components/Signature/SignatureGenerator/useSignatureGenerator';

interface Props extends FlexProps {
	controller: ReturnType<typeof useSignatureGenerator>;
}

const PEN_COLOR = defaultTheme.v3.colors.black[0];

export const SignatureGenerator = ({ controller, ...flexProps }: Props) => {
	const { selectedFont, setSelectedFont, signatureText, setSignatureText } = controller;

	useEffect(() => {
		const link = document.createElement('link');
		link.href =
			'https://fonts.googleapis.com/css2?family=Pacifico&family=Caveat&family=Marck+Script&family=Meddon&display=swap';
		link.rel = 'stylesheet';
		document.head.appendChild(link);
		return () => {
			document.head.removeChild(link);
		};
	}, []);

	return (
		<Flex direction="column" gap="1rem" bg={defaultTheme.v3.colors.white[0]} {...flexProps}>
			<StyledInput
				value={signatureText}
				onChange={(e) => setSignatureText(e.target.value)}
				placeholder="Signature"
				$fontFamily={selectedFont}
				$penColor={PEN_COLOR}
			/>

			<Radio.Group value={selectedFont} onChange={setSelectedFont}>
				<SimpleGrid
					cols={2}
					spacing="0"
					bg={defaultTheme.v3.colors.gray[0]}
					sx={{
						border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						borderRadius: '0.5rem',
					}}
				>
					{SIGNATURE_FONT_FAMILIES.map((font, index) => (
						<SignatureExamplesBox key={font} index={index}>
							<StyledRadio
								value={font}
								font={font}
								color={PEN_COLOR}
								label={signatureText || 'Signature'}
							/>
						</SignatureExamplesBox>
					))}
				</SimpleGrid>
			</Radio.Group>
		</Flex>
	);
};

const StyledInput = styled.input<{ $fontFamily: string; $penColor: string }>`
	font-family: ${({ $fontFamily }) => $fontFamily};
	color: ${({ $penColor }) => $penColor};
	font-size: 1.5rem;
	text-align: center;
	border-width: 1px;
	height: 4rem;
	min-width: 10rem;
	border-radius: 0.5rem;
	outline: none;
`;

const SignatureExamplesBox = styled(Flex)<FlexProps & { index: number }>`
	align-items: center;
	padding: 1rem;
	overflow: hidden;
	border-top: ${({ index, theme }) =>
		index >= 2 ? `1px solid ${theme.v3.colors.gray[4]}` : 'none'};
	border-left: ${({ index, theme }) =>
		index % 2 === 1 ? `1px solid ${theme.v3.colors.gray[4]}` : 'none'};
`;

const StyledRadio = styled(Radio)<RadioProps & { font: string; color: string }>`
	.mantine-Radio-inner {
		align-self: center;
		margin-right: 0.2rem;
	}

	.mantine-Radio-label {
		font-family: ${({ font }) => font};
		color: ${({ color }) => color};
		font-size: 1.2rem;
		line-height: 2.5rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		cursor: pointer;
	}
`;
